<template>
  <div class="page-wrapper">
    <HeaderComponent />

    <main class="main-content">
      <!-- Page Hero -->
      <section class="page-hero">
        <div class="container">
          <div class="hero-content">
            <nav class="breadcrumb-nav">
              <a href="/" class="breadcrumb-link">Home</a>
              <span class="breadcrumb-separator">/</span>
              <span class="breadcrumb-current">{{ pageCopy.breadcrumb }}</span>
            </nav>
            <h1 class="page-title">{{ pageCopy.title }}</h1>
            <p class="page-subtitle">{{ pageCopy.subtitle }}</p>
          </div>
        </div>
      </section>

      <!-- Main Content -->
      <section class="content-section">
        <div class="container">
          <div class="content-grid">
            <!-- Left Side - Info Panel -->
            <div class="info-panel">
              <div class="info-card">
                <div class="info-card-header">
                  <div class="info-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                      <rect x="1" y="3" width="15" height="13" rx="2"></rect>
                      <circle cx="5.5" cy="18.5" r="2.5"></circle>
                      <circle cx="12.5" cy="18.5" r="2.5"></circle>
                      <path d="M16 8h4l3 5v5h-2"></path>
                      <circle cx="19.5" cy="18.5" r="2.5"></circle>
                    </svg>
                  </div>
                  <h3>{{ pageCopy.breadcrumb }}</h3>
                </div>
                <p class="info-description">
                  {{ pageCopy.infoDescription }}
                </p>

                <div class="info-features">
                  <div class="feature-item">
                    <div class="feature-icon green">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                    <div>
                      <strong>M-Pesa Payment</strong>
                      <p>Pay instantly via mobile money</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon blue">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Instant Validation</strong>
                      <p>Digital receipt for enforcement</p>
                    </div>
                  </div>

                  <div class="feature-item">
                    <div class="feature-icon purple">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                    <div>
                      <strong>Flexible Duration</strong>
                      <p>Daily, weekly or monthly options</p>
                    </div>
                  </div>
                </div>

                <div class="info-help">
                  <h4>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <circle cx="12" cy="12" r="10"></circle>
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                      <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                    Parking Tips
                  </h4>
                  <ul>
                    <li>Pay before parking or within 15 minutes</li>
                    <li>Keep your receipt for verification</li>
                    <li>Different zones have different rates</li>
                    <li>Check signage for parking duration limits</li>
                  </ul>
                </div>
              </div>
            </div>

            <!-- Right Side - Form -->
            <div class="form-panel">
              <!-- Selection Form -->
              <div v-if="!showConfirmation" class="form-card">
                <div class="form-card-header">
                  <div class="step-badge">
                    <span class="step-number">1</span>
                    <span class="step-text">Enter Parking Details</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <p class="form-intro">
                    Select your parking duration, vehicle type, and enter your number plate to proceed with payment.
                  </p>

                  <div class="selection-form">
                    <!-- Parking Zone Selection — first so the customer picks
                         WHERE they're parked. The zone carries subCounty +
                         ward that feed the invoice for correct revenue slicing. -->
                    <div class="form-group">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                          <circle cx="12" cy="10" r="3"></circle>
                        </svg>
                        Parking Zone
                      </label>
                      <div class="select-wrapper">
                        <select v-model="parkingZone" class="form-select">
                          <option :value="null">-- Select where you're parked --</option>
                          <option
                              v-for="(z, i) in parkingZones"
                              :key="z.id || i"
                              :value="z"
                          >
                            {{ z.zone }}<span v-if="z.subCountyName"> — {{ z.subCountyName }}</span>
                          </option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                      <span v-if="parkingZone && parkingZone.wardName" class="form-hint">
                        {{ parkingZone.subCountyName }} · {{ parkingZone.wardName }}
                      </span>
                    </div>

                    <!-- Parking Duration Selection -->
                    <div class="form-group" :class="{ 'disabled': !parkingZone }">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        Parking Duration
                      </label>
                      <div class="select-wrapper">
                        <select v-model="incomeType" class="form-select" :disabled="!parkingZone">
                          <option :value="null">-- Select parking duration --</option>
                          <option
                              v-for="(item, index) in incomeTypes"
                              :key="index"
                              :value="item"
                          >
                            {{ item.incomeTypeDescription }}
                          </option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                      <span v-if="!parkingZone" class="form-hint">Select a parking zone first</span>
                    </div>

                    <!-- Vehicle Type Selection -->
                    <div class="form-group" :class="{ 'disabled': !incomeType }">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="1" y="3" width="15" height="13" rx="2"></rect>
                          <circle cx="5.5" cy="18.5" r="2.5"></circle>
                          <circle cx="12.5" cy="18.5" r="2.5"></circle>
                        </svg>
                        Vehicle Type
                      </label>
                      <div class="select-wrapper">
                        <select v-model="feesAndCharge" class="form-select" :disabled="!incomeType">
                          <option :value="null">-- Select vehicle type --</option>
                          <option
                              v-for="(item, index) in feesAndCharges"
                              :key="index"
                              :value="item"
                          >
                            {{ item.feeDescription }} - KES {{ formatNumber(item.unitFeeAmount) }}
                          </option>
                        </select>
                        <span class="select-icon">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="6 9 12 15 18 9"></polyline>
                          </svg>
                        </span>
                      </div>
                      <span v-if="!incomeType" class="form-hint">Select a parking duration first</span>
                    </div>

                    <!-- Fee Display Card -->
                    <transition name="slide-fade">
                      <div v-if="feesAndCharge" class="fee-display-card">
                        <div class="fee-header">
                          <span class="fee-label">Parking Fee</span>
                          <span class="fee-duration">{{ incomeType?.incomeTypeDescription }}</span>
                        </div>
                        <div class="fee-amount">
                          <span class="currency">KES</span>
                          <span class="amount">{{ formatNumber(feesAndCharge.unitFeeAmount) }}</span>
                        </div>
                        <div class="fee-details">
                          <span>{{ feesAndCharge.feeDescription }}</span>
                          <span v-if="feesAndCharge.zone">Zone: {{ feesAndCharge.zone }}</span>
                        </div>
                      </div>
                    </transition>

                    <!-- Number Plate Input -->
                    <div class="form-group" :class="{ 'disabled': !feesAndCharge }">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="2" y="7" width="20" height="10" rx="2"></rect>
                          <path d="M6 11h.01M10 11h4M18 11h.01"></path>
                        </svg>
                        Car Registration Number
                      </label>
                      <div class="input-with-icon">
                        <input
                            v-model="numberPlate"
                            type="text"
                            class="form-input uppercase"
                            placeholder="e.g., KAA 123A"
                            :disabled="!feesAndCharge"
                            @input="numberPlate = numberPlate.toUpperCase()"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="2" y="7" width="20" height="10" rx="2"></rect>
                          </svg>
                        </span>
                      </div>
                      <span v-if="!feesAndCharge" class="form-hint">Select a vehicle type first</span>
                    </div>

                    <!-- M-Pesa Phone Number -->
                    <div class="form-group" :class="{ 'disabled': !numberPlate }">
                      <label class="form-label">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                          <line x1="12" y1="18" x2="12.01" y2="18"></line>
                        </svg>
                        M-Pesa Phone Number
                      </label>
                      <div class="input-with-icon">
                        <input
                            v-model="phoneNumber"
                            type="tel"
                            class="form-input"
                            placeholder="e.g., 0712345678"
                            :disabled="!numberPlate"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                            <line x1="12" y1="18" x2="12.01" y2="18"></line>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">You'll receive an STK push to this number</span>
                    </div>

                    <!-- Error Message -->
                    <div v-if="message" class="alert alert-error">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="12"></line>
                        <line x1="12" y1="16" x2="12.01" y2="16"></line>
                      </svg>
                      <span>{{ message }}</span>
                      <button class="alert-close" @click="message = null">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                    </div>

                    <button
                        @click="proceedToCheckout"
                        class="btn btn-primary btn-block btn-lg"
                        :disabled="!canProceed"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <circle cx="9" cy="21" r="1"></circle>
                        <circle cx="20" cy="21" r="1"></circle>
                        <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                      </svg>
                      <span>Proceed to Checkout | KES {{ formatNumber(feesAndCharge?.unitFeeAmount || 0) }}</span>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Confirmation Form -->
              <div v-if="showConfirmation" class="form-card">
                <div class="form-card-header">
                  <button class="back-btn" @click="goBack">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="19" y1="12" x2="5" y2="12"></line>
                      <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                  </button>
                  <div class="step-badge">
                    <span class="step-number">2</span>
                    <span class="step-text">Confirm & Pay</span>
                  </div>
                </div>

                <div class="form-card-body">
                  <!-- Vehicle Header -->
                  <div class="vehicle-header">
                    <div class="vehicle-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <rect x="1" y="3" width="15" height="13" rx="2"></rect>
                        <circle cx="5.5" cy="18.5" r="2.5"></circle>
                        <circle cx="12.5" cy="18.5" r="2.5"></circle>
                        <path d="M16 8h4l3 5v5h-2"></path>
                        <circle cx="19.5" cy="18.5" r="2.5"></circle>
                      </svg>
                    </div>
                    <div class="vehicle-info">
                      <h2 class="number-plate-display">{{ numberPlate }}</h2>
                      <span class="vehicle-type-badge">{{ feesAndCharge.feeDescription }}</span>
                    </div>
                  </div>

                  <!-- Transaction Details -->
                  <div class="details-section">
                    <h3 class="section-title">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                      </svg>
                      Transaction Details
                    </h3>

                    <div class="details-grid">
                      <div class="detail-item" v-if="parkingZone || feesAndCharge.zone">
                        <span class="detail-label">Parking Zone</span>
                        <span class="detail-value">
                          {{ (parkingZone && parkingZone.zone) || feesAndCharge.zone }}
                          <small v-if="parkingZone && parkingZone.wardName" class="d-block text-muted">
                            {{ parkingZone.subCountyName }} · {{ parkingZone.wardName }}
                          </small>
                        </span>
                      </div>

                      <div class="detail-item">
                        <span class="detail-label">Duration</span>
                        <span class="detail-value">{{ incomeType.incomeTypeDescription }}</span>
                      </div>

                      <div class="detail-item">
                        <span class="detail-label">Vehicle Type</span>
                        <span class="detail-value">{{ feesAndCharge.feeDescription }}</span>
                      </div>

                      <div class="detail-item">
                        <span class="detail-label">Plate Number</span>
                        <span class="detail-value uppercase">{{ numberPlate }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Amount Card -->
                  <div class="amount-card">
                    <div class="amount-row total">
                      <span class="amount-label">Sub Total</span>
                      <span class="amount-value">KES {{ formatNumber(feesAndCharge.unitFeeAmount) }}</span>
                    </div>
                  </div>

                  <!-- Payment Form -->
                  <div class="payment-form">
                    <div class="form-group">
                      <label class="form-label">M-Pesa Phone Number</label>
                      <div class="input-with-icon">
                        <input
                            v-model="phoneNumber"
                            type="tel"
                            class="form-input"
                            placeholder="e.g., 0712345678"
                        />
                        <span class="input-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                            <line x1="12" y1="18" x2="12.01" y2="18"></line>
                          </svg>
                        </span>
                      </div>
                      <span class="form-hint">You'll receive an STK push to this number</span>
                    </div>

                    <button
                        @click="generateParkingPayment"
                        class="btn btn-success btn-block btn-lg"
                        :disabled="!phoneNumber || paymentLoading"
                    >
                      <div v-if="paymentLoading" class="spinner white"></div>
                      <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
                      </svg>
                      <span>{{ paymentLoading ? 'Processing...' : `Pay KES ${formatNumber(feesAndCharge.unitFeeAmount)}` }}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>

    <!-- Payment Modal -->
    <transition name="modal">
      <div v-if="payConfirmationPop" class="modal-overlay">
        <div class="modal-container payment-modal">
          <!-- Payment Header -->
          <div class="payment-modal-header" :class="paymentReceived ? 'success' : 'pending'">
            <div v-if="!paymentReceived" class="payment-loader">
              <div class="loader-ring"></div>
            </div>
            <div v-else class="payment-success-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <p class="payment-status-text">
              {{ paymentReceived ? 'Payment Successful!' : `Pending ${feesAndCharge?.feeDescription || 'Parking'} Payment` }}
            </p>
            <h2 class="payment-amount">KES {{ formatNumber(feesAndCharge?.unitFeeAmount || 0) }}</h2>
          </div>

          <!-- Payment Body -->
          <div class="payment-modal-body">
            <!-- Instructions (while waiting) -->
            <div v-if="!paymentReceived" class="payment-instructions">
              <h4>Payment Procedure</h4>
              <p class="instruction-subtitle">Follow the steps below to complete your payment</p>
              <ol>
                <li>
                  <span class="step-num">1</span>
                  Check for a <strong>payment pop up</strong> on your phone
                </li>
                <li>
                  <span class="step-num">2</span>
                  Enter your <strong>M-Pesa PIN</strong> and click OK
                </li>
                <li>
                  <span class="step-num">3</span>
                  Wait for <strong>M-Pesa confirmation SMS</strong>
                </li>
                <li>
                  <span class="step-num">4</span>
                  Wait up to <strong>45 seconds</strong> as we process your transaction
                </li>
              </ol>
              <div class="waiting-indicator">
                <div class="pulse-dot"></div>
                <span>Waiting for payment confirmation...</span>
              </div>
            </div>

            <!-- Success Content -->
            <div v-if="paymentReceived" class="payment-success">
              <h4>Thank You {{ transaction.names }}!</h4>
              <p class="success-message">Payment was successfully received.</p>

              <div class="success-details">
                <div class="success-row">
                  <span>Transaction Code</span>
                  <strong>{{ transaction.transaction_code }}</strong>
                </div>
                <div class="success-row">
                  <span>Number Plate</span>
                  <strong class="uppercase">{{ numberPlate }}</strong>
                </div>
                <div class="success-row" v-if="feesAndCharge.zone">
                  <span>Zone</span>
                  <strong>{{ feesAndCharge.zone }}</strong>
                </div>
                <div class="success-row">
                  <span>Vehicle Type</span>
                  <strong>{{ feesAndCharge.feeDescription }}</strong>
                </div>
              </div>

              <div class="parking-valid-card">
                <div class="valid-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <div class="valid-text">
                  <strong>Parking Valid</strong>
                  <span>{{ incomeType?.incomeTypeDescription }}</span>
                </div>
              </div>

              <div class="success-actions">
                <button @click="printReceipt" class="btn btn-primary">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6 9 6 2 18 2 18 9"></polyline>
                    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"></path>
                    <rect x="6" y="14" width="12" height="8"></rect>
                  </svg>
                  Print Receipt
                </button>
                <button @click="closePaymentModal" class="btn btn-secondary">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <FooterComponent />
  </div>
</template>

<script>
import { paysol, biller, execute, execute2, parking } from "@/api";
import HeaderComponent from "@/components/HeaderComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

export default {
  name: "DailyParking",
  components: { HeaderComponent, FooterComponent },
  data() {
    return {
      message: null,
      showConfirmation: false,
      paymentLoading: false,
      payConfirmationPop: false,
      loadingPayment: false,
      paymentReceived: false,

      numberPlate: '',
      phoneNumber: null,

      // Parking zones (loaded from countyAuthentication.zones via parking/getParkingZones).
      // Each zone carries its own subCountyID/subCountyName/wardID/wardName —
      // when the user picks a zone we fold those into the mint call so the
      // invoice lands in the correct revenue slice on /reports-streams.
      parkingZones: [],
      parkingZone: null,

      // Income Types (Duration)
      incomeTypes: [],
      incomeType: null,

      // Fees and Charges (Vehicle Types)
      feesAndCharges: [],
      feesAndCharge: null,

      // Billing
      billGenerated: {
        billNo: null,
        payBillNo: null,
        amount: null,
        success: null
      },

      transaction: {
        id: null,
        transaction_code: null,
        names: null,
        msisdn: null
      },

      receiptDetails: {
        receiptNo: null
      }
    };
  },

  computed: {
    canProceed() {
      return this.parkingZone &&
          this.incomeType &&
          this.feesAndCharge &&
          this.numberPlate &&
          this.numberPlate.length >= 4 &&
          this.phoneNumber;
    },
    // Route-driven page copy. This component is aliased to two routes:
    // - /daily-parking  → "Daily Parking Payment"
    // - /seasonal-parking → "Seasonal Parking Payment"
    // Everything else on the page (form, mint, STK) is identical; only the
    // hero + info-card headings change based on which route the user came in on.
    pageCopy() {
      const isSeasonal = this.$route && this.$route.name === 'seasonal-parking';
      if (isSeasonal) {
        return {
          title:      'Seasonal Parking Payment',
          subtitle:   'Buy a weekly, monthly or annual parking pass',
          breadcrumb: 'Seasonal Parking',
          infoDescription:
            "Purchase a longer-duration parking pass for your vehicle in designated county parking zones. Select your zone, pass duration, vehicle type, and pay instantly via M-Pesa."
        };
      }
      return {
        title:      'Daily Parking Payment',
        subtitle:   'Pay for street and off-street parking quickly and securely',
        breadcrumb: 'Daily Parking',
        infoDescription:
          "Pay for your vehicle's parking fee in designated county parking zones. Select your duration, vehicle type, and pay instantly via M-Pesa."
      };
    }
  },

  watch: {
    incomeType(val) {
      if (val && val.incomeTypeId) {
        this.feesAndCharge = null;
        this.getFeesAndCharges();
      }
    },
    // Keep the tab title in sync if the user hops between
    // /daily-parking and /seasonal-parking without a full page reload.
    '$route'() {
      if (this.pageCopy && this.pageCopy.title) {
        document.title = this.pageCopy.title;
      }
    }
  },

  mounted() {
    this.getIncomeTypes();
    this.getParkingZones();
    // Sync the browser tab title with the route-driven page copy on first paint.
    if (this.pageCopy && this.pageCopy.title) {
      document.title = this.pageCopy.title;
    }
  },

  methods: {
    formatNumber(num) {
      if (!num) return '0';
      return new Intl.NumberFormat('en-KE').format(num);
    },

    getParkingZones() {
      const data = new FormData();
      data.append("function", "getParkingZones");
      execute(data, parking)
          .then((res) => {
            if (res.data.success && res.data.data && Array.isArray(res.data.data.zones)) {
              // Sort by position (nulls last) then alphabetically so cities
              // with a curated order stay curated, and the rest fall in
              // predictable A-Z.
              this.parkingZones = [...res.data.data.zones].sort((a, b) => {
                const pa = a.position === null || a.position === undefined ? 9999 : Number(a.position);
                const pb = b.position === null || b.position === undefined ? 9999 : Number(b.position);
                if (pa !== pb) return pa - pb;
                return (a.zone || '').localeCompare(b.zone || '');
              });
            }
          })
          .catch(() => {
            // Non-fatal — user can still pay without zone context; we just
            // won't have geo-sliced revenue reports for their payment.
          });
    },

    proceedToCheckout() {
      if (!this.canProceed) {
        this.message = "Please fill in all required fields";
        return;
      }
      this.showConfirmation = true;
    },

    goBack() {
      this.showConfirmation = false;
      this.message = null;
    },

    closePaymentModal() {
      this.payConfirmationPop = false;
      this.paymentReceived = false;
      this.loadingPayment = false;
      // Reset form
      this.showConfirmation = false;
      this.numberPlate = '';
      this.phoneNumber = null;
      this.parkingZone = null;
      this.incomeType = null;
      this.feesAndCharge = null;
    },

    // API Methods
    getIncomeTypes() {
      const data = new FormData();
      data.append("function", "getIncomeTypes");
      data.append("incomeTypePrefix", "PKN");

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.incomeTypes = res.data.data.incomeTypes;
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.message = e.message;
          });
    },

    getFeesAndCharges() {
      this.feesAndCharges = [];
      const data = new FormData();
      data.append("function", "getFeesAndCharges");
      data.append("incomeTypeId", this.incomeType.incomeTypeId);

      execute(data, biller)
          .then((res) => {
            if (res.data.success) {
              this.feesAndCharges = res.data.data.feesAndCharges;
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.message = e.message;
          });
    },

    generateParkingPayment() {
      if (!this.phoneNumber) {
        this.message = "Please enter your M-Pesa phone number";
        return;
      }

      this.paymentLoading = true;

      const data = new FormData();
      data.append("function", "generateParkingPayment");
      data.append("numberPlate", this.numberPlate);
      data.append("feeId", this.feesAndCharge.feeId);
      // Use the selected parking zone's identity (falls back to the fee's
      // own zone label if for any reason parkingZone isn't set — should
      // never happen since canProceed gates on it).
      data.append("zone", (this.parkingZone && this.parkingZone.zone) || this.feesAndCharge.zone || "");
      data.append("names", "Self Service");
      data.append("phoneNumber", this.phoneNumber);
      data.append("idNo", sessionStorage.getItem("sessionIdNo") || "");
      data.append("username", sessionStorage.getItem("sessionNames") || "");
      // Zone-derived geo: subCounty + ward flow into countyBiller so /reports-streams
      // can slice this payment correctly.
      data.append("subCountyID",   (this.parkingZone && this.parkingZone.subCountyID) || "");
      data.append("subCountyName", (this.parkingZone && this.parkingZone.subCountyName) || "");
      data.append("wardID",        (this.parkingZone && this.parkingZone.wardID) || "");
      data.append("wardName",      (this.parkingZone && this.parkingZone.wardName) || "");
      data.append("customerPhoneNumber", this.phoneNumber);
      data.append("description", `Parking - ${this.numberPlate}`);

      execute(data, parking)
          .then((res) => {
            this.paymentLoading = false;
            if (res.data.success) {
              this.billGenerated = res.data.data.billGenerated;
              this.customerPayBillOnline();
            } else {
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.paymentLoading = false;
            this.message = e.message;
          });
    },

    customerPayBillOnline() {
      this.payConfirmationPop = true;
      this.loadingPayment = true;
      this.paymentReceived = false;

      const data = new FormData();
      data.append("function", "customerPayBillOnline");
      data.append("payBillNumber", this.billGenerated.payBillNo);
      data.append("amount", this.billGenerated.amount);
      data.append("accountReference", this.billGenerated.billNo);
      data.append("transactionDesc", this.billGenerated.billNo);
      data.append("phoneNumber", this.phoneNumber);
      data.append("token", "im05WXYH2rwRruPjCICieOs8m4E8IoltnDEhyPUv6bnB9cU60gD48SnJPC6oh7EpsPaAUGC8wqIdtVVjGlWLxqFssshxMHxHjEQJ");

      execute2(data, paysol)
          .then((res) => {
            if (res.data.success) {
              const intervalId = setInterval(() => {
                this.checkPayment()
                    .then((result) => {
                      if (result) {
                        clearInterval(intervalId);
                      }
                    })
                    .catch((error) => {
                      console.error(error);
                    });
              }, 2000);
            } else {
              this.loadingPayment = false;
              this.message = res.data.message;
            }
          })
          .catch((e) => {
            this.loadingPayment = false;
            this.message = e.message;
          });
    },

    checkPayment() {
      const data = new FormData();
      data.append("function", "checkPayment");
      data.append("accountRef", this.billGenerated.billNo);

      return new Promise((resolve, reject) => {
        execute(data, biller)
            .then((res) => {
              if (res.data.success) {
                this.loadingPayment = false;
                this.transaction = res.data.data.transaction;
                this.receiptDetails = res.data.data.receipt.receiptDetails;
                this.paymentReceived = true;
                resolve(true);
              } else {
                resolve(false);
              }
            })
            .catch((error) => {
              reject(error);
            });
      });
    },

    printReceipt() {
      if (!this.receiptDetails.receiptNo) {
        alert("Receipt not available yet. Please try again.");
        return;
      }

      localStorage['params'] = JSON.stringify({
        receiptNo: this.receiptDetails.receiptNo
      });
      const routeData = this.$router.resolve({ name: 'receipt' });
      window.open(routeData.href, '_blank');
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');

.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f8fafc;
}

.main-content {
  flex: 1;
  padding-top: 72px;
}

/* Page Hero */
.page-hero {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  padding: 48px 0;
  color: white;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.breadcrumb-nav {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-size: 14px;
}

.breadcrumb-link {
  color: rgba(255,255,255,0.7);
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumb-link:hover { color: white; }
.breadcrumb-separator { color: rgba(255,255,255,0.4); }
.breadcrumb-current { color: white; }

.page-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 32px;
  font-weight: 800;
  margin-bottom: 8px;
}

.page-subtitle {
  opacity: 0.9;
  font-size: 16px;
}

/* Content Section */
.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 32px;
  align-items: start;
}

/* Info Panel */
.info-panel {
  position: sticky;
  top: 96px;
}

.info-card {
  background: white;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.info-icon {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
}

.info-card-header h3 {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1e293b;
}

.info-description {
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
}

.info-features {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
}

.feature-item {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.feature-icon.green { background: #d1fae5; color: #059669; }
.feature-icon.blue { background: #dbeafe; color: #2563eb; }
.feature-icon.purple { background: #ede9fe; color: #7c3aed; }

.feature-item strong {
  display: block;
  font-size: 14px;
  color: #1e293b;
  margin-bottom: 2px;
}

.feature-item p {
  font-size: 13px;
  color: #64748b;
  margin: 0;
}

.info-help {
  padding: 20px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  border-radius: 12px;
}

.info-help h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e40af;
  margin-bottom: 12px;
}

.info-help ul {
  margin: 0;
  padding-left: 24px;
}

.info-help li {
  font-size: 13px;
  color: #3b82f6;
  margin-bottom: 6px;
}

/* Form Panel */
.form-card {
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
  overflow: hidden;
}

.form-card-header {
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.step-badge {
  display: flex;
  align-items: center;
  gap: 10px;
}

.step-badge .step-number {
  width: 28px;
  height: 28px;
  background: #0f4c81;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 13px;
}

.step-badge .step-text {
  font-weight: 600;
  color: #1e293b;
}

.back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.back-btn:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.form-card-body {
  padding: 32px;
}

.form-intro {
  color: #64748b;
  margin-bottom: 24px;
  line-height: 1.6;
}

/* Form Elements */
.form-group {
  margin-bottom: 20px;
}

.form-group.disabled {
  opacity: 0.6;
}

.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 10px;
}

.select-wrapper {
  position: relative;
}

.form-select {
  width: 100%;
  padding: 14px 44px 14px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  background: white;
  cursor: pointer;
  appearance: none;
  transition: all 0.2s;
}

.form-select:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.form-select:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.select-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.input-with-icon {
  position: relative;
}

.form-input {
  width: 100%;
  padding: 14px 16px;
  padding-right: 48px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
}

.form-input:focus {
  outline: none;
  border-color: #0f4c81;
  box-shadow: 0 0 0 4px rgba(15, 76, 129, 0.1);
}

.form-input:disabled {
  background: #f8fafc;
  cursor: not-allowed;
}

.form-input.uppercase {
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 1px;
}

.input-icon {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.form-hint {
  display: block;
  margin-top: 8px;
  font-size: 13px;
  color: #64748b;
}

/* Fee Display Card */
.fee-display-card {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
  border-radius: 14px;
  padding: 24px;
  color: white;
  margin-bottom: 24px;
}

.fee-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.fee-label {
  font-size: 13px;
  opacity: 0.8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.fee-duration {
  background: rgba(255,255,255,0.2);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.fee-amount {
  margin-bottom: 12px;
}

.fee-amount .currency {
  font-size: 16px;
  opacity: 0.9;
  margin-right: 4px;
}

.fee-amount .amount {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
}

.fee-details {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  opacity: 0.8;
  padding-top: 12px;
  border-top: 1px solid rgba(255,255,255,0.2);
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 10px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
}

.alert-close {
  margin-left: auto;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: inherit;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 24px;
  border-radius: 10px;
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
}

.btn-block { width: 100%; }
.btn-lg { padding: 16px 32px; font-size: 16px; }

.btn-primary {
  background: #0f4c81;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #0a3459;
}

.btn-success {
  background: #059669;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background: #047857;
}

.btn-secondary {
  background: #f1f5f9;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #e2e8f0;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Spinner */
.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0,0,0,0.1);
  border-top-color: currentColor;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.spinner.white {
  border-color: rgba(255,255,255,0.3);
  border-top-color: white;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Vehicle Header */
.vehicle-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-radius: 14px;
  margin-bottom: 24px;
}

.vehicle-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}

.number-plate-display {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 4px;
  letter-spacing: 2px;
}

.vehicle-type-badge {
  font-size: 14px;
  color: #2563eb;
  font-weight: 600;
  background: rgba(37, 99, 235, 0.15);
  padding: 4px 10px;
  border-radius: 6px;
}

/* Details Section */
.details-section {
  margin-bottom: 24px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 16px;
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.detail-item {
  padding: 14px;
  background: #f8fafc;
  border-radius: 10px;
}

.detail-label {
  display: block;
  font-size: 11px;
  color: #64748b;
  margin-bottom: 4px;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
}

.detail-value.uppercase {
  text-transform: uppercase;
}

/* Amount Card */
.amount-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  border: 2px solid #fbbf24;
  border-radius: 14px;
  padding: 24px;
  margin-bottom: 24px;
}

.amount-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.amount-row.total {
  padding: 8px 0;
}

.amount-label {
  font-size: 16px;
  font-weight: 600;
  color: #92400e;
}

.amount-value {
  font-size: 24px;
  font-weight: 700;
  color: #78350f;
}

/* Payment Form */
.payment-form {
  padding-top: 24px;
  border-top: 1px solid #e2e8f0;
}

/* Payment Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 24px;
  backdrop-filter: blur(4px);
}

.modal-container {
  background: white;
  border-radius: 20px;
  max-width: 440px;
  width: 100%;
  overflow: hidden;
}

.payment-modal-header {
  padding: 40px 32px;
  text-align: center;
  color: white;
}

.payment-modal-header.pending {
  background: linear-gradient(135deg, #0f4c81 0%, #0a3459 100%);
}

.payment-modal-header.success {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.payment-loader {
  margin-bottom: 20px;
}

.loader-ring {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(255,255,255,0.2);
  border-top-color: white;
  border-radius: 50%;
  margin: 0 auto;
  animation: spin 1s linear infinite;
}

.payment-success-icon {
  width: 80px;
  height: 80px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.payment-status-text {
  font-size: 14px;
  opacity: 0.9;
  margin-bottom: 8px;
}

.payment-amount {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 36px;
  font-weight: 800;
}

.payment-modal-body {
  padding: 32px;
}

.payment-instructions h4 {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.instruction-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 20px;
}

.payment-instructions ol {
  list-style: none;
  padding: 0;
  margin: 0 0 24px;
}

.payment-instructions li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
  font-size: 14px;
  color: #475569;
}

.payment-instructions li:last-child {
  border-bottom: none;
}

.step-num {
  width: 24px;
  height: 24px;
  background: #eff6ff;
  color: #2563eb;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.waiting-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 16px;
  background: #fffbeb;
  border-radius: 10px;
  font-size: 14px;
  color: #92400e;
}

.pulse-dot {
  width: 10px;
  height: 10px;
  background: #f59e0b;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(1.2); }
}

.payment-success h4 {
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
}

.success-message {
  font-size: 14px;
  color: #64748b;
  margin-bottom: 20px;
}

.success-details {
  background: #f8fafc;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 16px;
  text-align: left;
}

.success-row {
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #e2e8f0;
  font-size: 14px;
}

.success-row:last-child {
  border-bottom: none;
}

.success-row span {
  color: #64748b;
}

.success-row strong {
  color: #1e293b;
}

.success-row strong.uppercase {
  text-transform: uppercase;
}

.parking-valid-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: #d1fae5;
  border: 1px solid #6ee7b7;
  border-radius: 10px;
  margin-bottom: 24px;
}

.valid-icon {
  width: 40px;
  height: 40px;
  background: #059669;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.valid-text {
  text-align: left;
}

.valid-text strong {
  display: block;
  color: #047857;
  font-size: 15px;
}

.valid-text span {
  font-size: 13px;
  color: #059669;
}

.success-actions {
  display: flex;
  gap: 12px;
}

.success-actions .btn {
  flex: 1;
}

/* Transitions */
.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.95) translateY(20px);
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.2s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Responsive */
@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .info-panel {
    position: static;
  }
}

@media (max-width: 768px) {
  /* Put the FORM first, info-panel below. Users came here to pay, not to
     read tips. `order` on flexbox children doesn't work in grid, so flip
     the grid rows and re-assign explicitly. */
  .content-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .info-panel { order: 2; }
  .form-panel { order: 1; }

  /* Shrink the giant hero. */
  .container { padding: 0 12px; }
  .page-hero { padding: 20px 0 40px; }
  .page-title { font-size: 20px !important; margin-bottom: 4px; }
  .breadcrumb-current,
  .breadcrumb { font-size: 12px; }
  .content-section { padding: 24px 0 32px; }

  /* Info card — now BELOW the form, so make it compact/collapsible-looking
     without being distracting. */
  .info-card { padding: 16px 14px; border-radius: 12px; }
  .info-card-header { gap: 10px; }
  .info-icon { width: 40px; height: 40px; }
  .info-icon svg { width: 20px; height: 20px; }
  .info-card h3 { font-size: 16px; }
  .info-card .card-subtitle,
  .info-card p { font-size: 12.5px; line-height: 1.4; }
  .info-features { gap: 8px; margin-top: 12px; }
  .info-feature { padding: 10px; gap: 8px; }
  .info-feature-icon { width: 28px; height: 28px; }
  .info-feature-icon svg { width: 14px; height: 14px; }
  .info-feature strong { font-size: 12.5px; }
  .info-feature .text-muted,
  .info-feature p { font-size: 11px; margin-top: 2px; }
  .tips-card { padding: 14px; border-radius: 10px; margin-top: 12px; }
  .tips-card h4,
  .tips-card .tips-title { font-size: 13px; margin-bottom: 6px; }
  .tips-card ul { padding-left: 16px; margin: 0; }
  .tips-card li { font-size: 12px; line-height: 1.5; }

  /* Form-card chrome — the STAR of this screen on mobile. */
  .form-card { border-radius: 12px; }
  .form-card-header { padding: 12px 14px; }
  .form-card-header .step-text { font-size: 14px; }
  .form-card-body {
    padding: 16px 14px;
  }
  .form-intro { font-size: 12.5px; margin-bottom: 12px; }
  .form-group { margin-bottom: 12px; }
  .form-label { font-size: 12.5px; margin-bottom: 4px; }
  .form-select,
  .form-input { font-size: 14px; padding: 10px 12px; min-height: 42px; }
  .form-hint { font-size: 11px; }

  .details-grid {
    grid-template-columns: 1fr;
  }

  .vehicle-header {
    flex-direction: column;
    text-align: center;
  }

  .success-actions {
    flex-direction: column;
  }

  .fee-display-card {
    padding: 16px;
  }

  .fee-amount .amount {
    font-size: 24px;
  }
}
</style>